import { http } from "@/lib/http";

import type { GrantResource } from "@/features/queues/types";

import type {
  DeliveryResource,
  Paginated,
  PublishRequest,
  PublishResponse,
  SubscriptionCreateRequest,
  SubscriptionResource,
  TopicCreateRequest,
  TopicResource,
} from "./types";

export async function listTopics(params: {
  page: number;
  pageSize: number;
  name?: string;
}): Promise<Paginated<TopicResource>> {
  const { data } = await http.get<Paginated<TopicResource>>("/topics", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      name: params.name || undefined,
    },
  });
  return data;
}

export async function getTopic(id: string): Promise<TopicResource> {
  const { data } = await http.get<TopicResource>(`/topics/${id}`);
  return data;
}

export async function createTopic(
  payload: TopicCreateRequest,
): Promise<TopicResource> {
  const { data } = await http.post<TopicResource>("/topics", payload);
  return data;
}

export async function updateTopic(
  id: string,
  payload: TopicCreateRequest,
): Promise<TopicResource> {
  const { data } = await http.put<TopicResource>(`/topics/${id}`, payload);
  return data;
}

export async function deleteTopic(id: string): Promise<void> {
  await http.delete(`/topics/${id}`);
}

export async function publishMessage(
  id: string,
  payload: PublishRequest,
): Promise<PublishResponse> {
  const { data } = await http.post<PublishResponse>(
    `/topics/${id}/publish`,
    payload,
  );
  return data;
}

export async function listSubscriptions(
  id: string,
): Promise<SubscriptionResource[]> {
  const { data } = await http.get<SubscriptionResource[]>(
    `/topics/${id}/subscriptions`,
  );
  return data;
}

export async function addSubscription(
  id: string,
  payload: SubscriptionCreateRequest,
): Promise<SubscriptionResource> {
  const { data } = await http.post<SubscriptionResource>(
    `/topics/${id}/subscriptions`,
    payload,
  );
  return data;
}

export async function deleteSubscription(
  id: string,
  subscriptionId: string,
): Promise<void> {
  await http.delete(`/topics/${id}/subscriptions/${subscriptionId}`);
}

export async function enableSubscription(
  id: string,
  subscriptionId: string,
): Promise<SubscriptionResource> {
  const { data } = await http.post<SubscriptionResource>(
    `/topics/${id}/subscriptions/${subscriptionId}/enable`,
  );
  return data;
}

export async function listDeliveries(
  id: string,
  params: { page: number; pageSize: number; status?: string },
): Promise<Paginated<DeliveryResource>> {
  const { data } = await http.get<Paginated<DeliveryResource>>(
    `/topics/${id}/deliveries`,
    {
      params: {
        page: params.page,
        page_size: params.pageSize,
        status: params.status || undefined,
      },
    },
  );
  return data;
}

export async function listTopicGrants(
  id: string,
): Promise<GrantResource[]> {
  const { data } = await http.get<GrantResource[]>(`/topics/${id}/grants`);
  return data;
}

export async function createTopicGrant(
  id: string,
  granteeProjectId: string,
  actions: string[],
): Promise<GrantResource> {
  const { data } = await http.post<GrantResource>(`/topics/${id}/grants`, {
    grantee_project_id: granteeProjectId,
    actions,
  });
  return data;
}

export async function deleteTopicGrant(
  id: string,
  grantId: string,
): Promise<void> {
  await http.delete(`/topics/${id}/grants/${grantId}`);
}
